import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { logFoodAPI } from '../../../utils/api';
import RecentlyLoggedList from '../../../components/logfood/RecentlyLoggedList';
import FoodResultRow from '../../../components/logfood/FoodResultRow';
import FallbackChips from '../../../components/logfood/FallbackChips';
import FatSecretAttribution from '../../../components/logfood/FatSecretAttribution';
import { searchItemToFood } from '../../../utils/logFoodMappers';

const PAGE_SIZE = 10;
const ENABLED = process.env.REACT_APP_ENABLE_FATSECRET === 'true';

/**
 * Foods tab — FatSecret-backed mixed whole + branded foods.
 *
 * When ENABLE_FATSECRET=false, this tab self-disables (parent passes
 * `visibleTabs` to TabStrip which already filters us out). The component
 * still renders if forced because the dev wants to verify the empty
 * disabled state.
 */
export default function FoodsTab({
  recents,
  recentsLoading,
  onSelectFood,
  onSelectRecent,
  onQuickLog,
  onQuickLogRecent,
  onTryTab,
  onCreateManual,
  onSearchAllRecents,
}) {
  const [query, setQuery] = useState('');
  const [packagedOnly, setPackagedOnly] = useState(false);
  const [results, setResults] = useState([]);
  const [matchingRecents, setMatchingRecents] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(function (q, packaged, pg) {
    if (!q || !q.trim()) {
      setResults([]);
      setMatchingRecents([]);
      setTotal(0);
      setFallback(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const params = { q: q, packaged_only: packaged, page: pg, limit: PAGE_SIZE };
    Promise.all([
      logFoodAPI.searchFoods(params).catch(function (err) {
        return { error: err };
      }),
      logFoodAPI.getRecentFoods({ scope: 'foods', q: q, limit: 3 }).catch(function () {
        return { data: [] };
      }),
    ]).then(function (resArr) {
      const searchRes = resArr[0];
      const recentsRes = resArr[1];

      if (searchRes && searchRes.error) {
        const status = searchRes.error.response && searchRes.error.response.status;
        const detail = (searchRes.error.response && searchRes.error.response.data && searchRes.error.response.data.detail) || '';
        console.error('[FoodsTab] search/foods failed:', { status, detail, message: searchRes.error.message });
        if (status === 503) {
          setError('disabled');
        } else if (status === 401) {
          setError('auth');
        } else if (status === 402) {
          setError('failed');
        } else if (status) {
          setError('http_' + status);
        } else {
          setError('network');
        }
        setResults([]);
        setTotal(0);
        setFallback(null);
      } else {
        const data = (searchRes && searchRes.data) || {};
        const items = Array.isArray(data.items) ? data.items : [];
        if (pg === 0) {
          setResults(items);
        } else {
          setResults(function (prev) { return prev.concat(items); });
        }
        setTotal(Number(data.total || 0));
        setFallback(data.fallback_suggestion || null);
      }
      setMatchingRecents(Array.isArray(recentsRes.data) ? recentsRes.data.slice(0, 3) : []);
      setLoading(false);
    });
  }, []);

  // Debounce query changes (300ms) and reset to page 0 on new query/toggle.
  useEffect(function () {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPage(0);
    debounceRef.current = setTimeout(function () {
      doSearch(query, packagedOnly, 0);
    }, 300);
    return function () { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, packagedOnly, doSearch]);

  const handleShowMore = function () {
    const next = page + 1;
    setPage(next);
    doSearch(query, packagedOnly, next);
  };

  const isLanding = !query.trim();
  const showRecentsSubsection = matchingRecents.length > 0 && !loading;

  return (
    <div className="flex flex-col" data-testid="foods-tab">
      {/* Search bar */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            placeholder="Search foods..."
            className="w-full pl-9 pr-9 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="foods-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={function () { setQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 size-5 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center"
              aria-label="Clear search"
              data-testid="foods-search-clear"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {/* Packaged Only toggle */}
      <label
        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground cursor-pointer"
        data-testid="foods-packaged-toggle-label"
      >
        <input
          type="checkbox"
          checked={packagedOnly}
          onChange={function (e) { setPackagedOnly(e.target.checked); }}
          className="size-4 rounded border-input"
          data-testid="foods-packaged-toggle"
        />
        Show Packaged Only
      </label>

      {/* Content */}
      {!ENABLED && (
        <div className="px-4 py-12 text-center text-sm text-muted-foreground">
          Food search activates when FatSecret keys are connected.
        </div>
      )}

      {ENABLED && isLanding && (
        <RecentlyLoggedList
          items={recents}
          onSelect={onSelectRecent}
          onQuickLog={onQuickLogRecent}
          emptyMessage={recentsLoading ? 'Loading…' : 'No recently logged foods yet.'}
        />
      )}

      {ENABLED && !isLanding && (
        <>
          {showRecentsSubsection && (
            <RecentlyLoggedList
              items={matchingRecents}
              onSelect={onSelectRecent}
              onQuickLog={onQuickLogRecent}
              trailingAction={
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={function () { setQuery(''); }}
                  data-testid="foods-search-all-recents"
                >
                  Show all recents →
                </button>
              }
            />
          )}

          {loading && results.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && error === 'disabled' && (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              Food search activates when FatSecret keys are connected.
            </div>
          )}

          {!loading && error === 'auth' && (
            <div className="px-4 py-12 text-center text-sm text-destructive" data-testid="foods-search-error-auth">
              Session expired — please sign in again.
            </div>
          )}

          {!loading && error === 'network' && (
            <div className="px-4 py-12 text-center text-sm text-destructive" data-testid="foods-search-error-network">
              Network unreachable. Check your connection and try again.
            </div>
          )}

          {!loading && typeof error === 'string' && error.indexOf('http_') === 0 && (
            <div className="px-4 py-12 text-center text-sm text-destructive" data-testid="foods-search-error-http">
              Search failed ({error.replace('http_', 'HTTP ')}). Please try again.
            </div>
          )}

          {!loading && error === 'failed' && (
            <div className="px-4 py-12 text-center text-sm text-destructive">
              Search failed. Please try again.
            </div>
          )}

          {!loading && !error && results.length > 0 && (
            <>
              <div className="px-4 py-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Results
                </h3>
              </div>
              <ul className="divide-y divide-border">
                {results.map(function (item) {
                  return (
                    <FoodResultRow
                      key={item.id || item.external_id}
                      item={item}
                      onSelect={function () { onSelectFood(searchItemToFood(item)); }}
                      onQuickLog={function () { onQuickLog(searchItemToFood(item)); }}
                    />
                  );
                })}
              </ul>
              {results.length < total && (
                <div className="px-4 py-3 flex justify-center">
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={handleShowMore}
                    disabled={loading}
                    data-testid="foods-show-more"
                  >
                    {loading ? 'Loading…' : 'Show 10 more results'}
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && !error && results.length === 0 && fallback && (
            <FallbackChips
              suggestion={fallback}
              query={query}
              onTryTab={onTryTab}
              onCreateManual={onCreateManual}
            />
          )}
        </>
      )}

      {ENABLED && <FatSecretAttribution />}
    </div>
  );
}
